import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { resolveGlobalDisclaimers } from "../../../services/disclaimersService";
import DisclaimersPopup from "./DisclaimersPopup";

// Keys per resolve request. Well under the API's limit; a page view normally
// needs one request (a vehicle overview cites ~25 distinct keys).
const RESOLVE_CHUNK_SIZE = 200;

// 1. Create a new context that will hold our state
//
// Exported so a caller can read it WITHOUT the throwing hook below. Most consumers
// want the throw — being rendered outside the provider is a wiring mistake and
// should say so. A component that only wants to contribute disclaimers when a
// provider happens to be present is the exception, and has no way to ask otherwise.
export const DisclaimersContext = createContext();

// 2. This provider will wrap our app and share state/functions with all children
export const DisclaimersProvider = ({ children }) => {
  // Every disclaimer is a global one (ES-13686): an authored key or a VAPI code.
  // Stars register their keys as they render and a page's keys are resolved in
  // one POST /disclaimers/resolve request; nothing downloads the whole list.
  //
  // Each key carries a status, so the popup can tell its three empty states
  // apart:
  //   loading   requested, no answer yet
  //   resolved  the global list has it (text in globalDisclaimers)
  //   missing   the global list does not have it
  //   failed    could not be checked (network, or VAPI down); registering the
  //             key again retries it
  const [globalDisclaimers, setGlobalDisclaimers] = useState({});
  const [activeDisclaimerId, setActiveDisclaimerId] = useState(null);
  const [globalStatus, setGlobalStatus] = useState({});

  // Synchronous mirrors: many stars register in one commit, before any of
  // their state updates have rendered.
  const statusRef = useRef({});
  const queuedRef = useRef(new Set());
  const flushTimerRef = useRef(null);

  const setStatuses = useCallback((keys, status) => {
    if (!keys.length) {
      return;
    }
    for (const key of keys) {
      statusRef.current[key] = status;
    }
    setGlobalStatus((prev) => {
      const next = { ...prev };
      for (const key of keys) {
        next[key] = status;
      }
      return next;
    });
  }, []);

  const resolveChunk = useCallback(
    async (keys) => {
      try {
        const { disclaimers: found, vapiAvailable } =
          await resolveGlobalDisclaimers(keys);
        setGlobalDisclaimers((prev) => ({ ...prev, ...found }));
        setStatuses(
          keys.filter((key) => key in found),
          "resolved",
        );
        setStatuses(
          keys.filter((key) => !(key in found)),
          vapiAvailable ? "missing" : "failed",
        );
      } catch (error) {
        console.error("Failed to resolve global disclaimers", error);
        setStatuses(keys, "failed");
      }
    },
    [setStatuses],
  );

  const flushQueued = useCallback(() => {
    flushTimerRef.current = null;
    const keys = [...queuedRef.current];
    queuedRef.current.clear();
    for (let i = 0; i < keys.length; i += RESOLVE_CHUNK_SIZE) {
      resolveChunk(keys.slice(i, i + RESOLVE_CHUNK_SIZE));
    }
  }, [resolveChunk]);

  // Everything queued goes out as one request on the next tick.
  const sendQueued = useCallback(() => {
    if (!queuedRef.current.size) {
      return;
    }
    setStatuses([...queuedRef.current], "loading");
    if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flushQueued, 0);
    }
  }, [flushQueued, setStatuses]);

  /**
   * Ask for these keys' global answers. Cheap to call repeatedly: a key already
   * loading, resolved or missing is skipped, and everything registered in the
   * same tick is sent as one request. A key that failed is retried.
   */
  const requestDisclaimers = useCallback(
    (ids) => {
      for (const id of ids ?? []) {
        if (!id || queuedRef.current.has(id)) {
          continue;
        }
        const status = statusRef.current[id];
        if (status && status !== "failed") {
          continue;
        }
        queuedRef.current.add(id);
      }
      sendQueued();
    },
    [sendQueued],
  );

  useEffect(() => () => clearTimeout(flushTimerRef.current), []);

  const openDisclaimer = useCallback(
    (id) => {
      setActiveDisclaimerId(id);
      // A popup can be opened for a key no star registered, and a failed key
      // deserves a retry.
      requestDisclaimers([id]);
    },
    [requestDisclaimers],
  );
  const closeDisclaimer = useCallback(() => setActiveDisclaimerId(null), []);

  const resolveDisclaimer = useCallback(
    (id) => globalDisclaimers[id],
    [globalDisclaimers],
  );

  const disclaimerStatus = useCallback(
    (id) => globalStatus[id],
    [globalStatus],
  );

  // Memoised, or the stable callbacks above buy nothing for consumers.
  //
  // An inline `value={{...}}` is a new object every provider render, and this
  // provider sits at app level — so every consumer of this context re-rendered
  // whenever anything above it did, including on window resize, whether or not a
  // disclaimer had changed. `DisclaimerStar` is many instances per page.
  //
  // It also leaves the original hazard in place for anyone who depends on the
  // context OBJECT rather than a destructured field: that identity would still
  // change every render, and the loop comes back. Stabilising the functions but
  // not the value they travel in only fixes the callers who happened to
  // destructure.
  const value = useMemo(
    () => ({
      requestDisclaimers,
      resolveDisclaimer,
      disclaimerStatus,
      activeDisclaimerId,
      openDisclaimer,
      closeDisclaimer,
    }),
    [
      requestDisclaimers,
      resolveDisclaimer,
      disclaimerStatus,
      activeDisclaimerId,
      openDisclaimer,
      closeDisclaimer,
    ],
  );

  return (
    <DisclaimersContext.Provider value={value}>
      {children}
      {/* The popup is always mounted, but only shows when an ID is set */}
      <DisclaimersPopup />
    </DisclaimersContext.Provider>
  );
};

// 3. Hook to use context from any child component
export const useDisclaimersContext = () => {
  const context = useContext(DisclaimersContext);
  if (!context) {
    throw new Error(
      "useDisclaimersContext must be used within a DisclaimersProvider",
    );
  }
  return context;
};
